// Contracts — work.weern.com EVENT-TICKET PDF pipeline (2026-06-24). A generator
// (later: team) uploads an event-ticket PDF on the platform; the GCP
// `pothos-ticket-pipeline` function runs the methodology and writes back ONE
// manifest. Codes are the load-bearing field (decoder-exact); semantic fields are
// best-effort with flags. Shapes from migrations/0010 + docs/adr/0028.
import { z } from 'zod';

// One decoded code. `type` = zxing symbology name (QRCode/Code128/EAN13/…).
export const WorkTicketCodeSchema = z.object({
  type: z.string(),
  value: z.string(),
});
export type WorkTicketCode = z.infer<typeof WorkTicketCodeSchema>;

// POST /api/work/generator/uploadTicketPdf — upload an event-ticket PDF.
export const WorkTicketUploadReqSchema = z.object({
  fileBase64: z.string().min(1),
  fileName: z.string().trim().default('ticket.pdf'),
  mimeType: z.string().trim().default('application/pdf'),
  locale_hint: z.string().trim().default(''),
});
export type WorkTicketUploadReq = z.infer<typeof WorkTicketUploadReqSchema>;

export const WorkTicketUploadResSchema = z.object({
  ok: z.boolean(),
  id: z.string(),
  status: z.string(),       // pending | processing | done | review | error
  deduped: z.boolean(),     // true → identical content_hash already uploaded
});
export type WorkTicketUploadRes = z.infer<typeof WorkTicketUploadResSchema>;

// POST /api/work/_admin/ticketManifest — pipeline write-back. Semantic string
// fields default '' to match the D1 NOT NULL '' columns; codes/flags default [].
export const WorkTicketManifestReqSchema = z.object({
  job_id: z.string().trim().min(1),
  status: z.enum(['done', 'review']).default('review'),
  codes: z.array(WorkTicketCodeSchema).default([]),
  checksum_ok: z.boolean().nullable().default(null),
  attendee_name: z.string().default(''),
  nominative: z.enum(['yes', 'no']).default('no'),
  event_name: z.string().default(''),
  event_date: z.string().default(''),
  event_time: z.string().default(''),
  venue_name: z.string().default(''),
  venue_address: z.string().default(''),
  city: z.string().default(''),
  country: z.string().default(''),
  organizer: z.string().default(''),
  category: z.string().default(''),
  section: z.string().default(''),
  seat_row: z.string().default(''),
  seat: z.string().default(''),
  gate: z.string().default(''),
  price: z.string().default(''),
  currency: z.string().default(''),
  language: z.string().default(''),
  notes: z.string().default(''),
  flags: z.array(z.string()).default([]),
  provenance: z.record(z.string(), z.string()).default({}),
});
export type WorkTicketManifestReq = z.infer<typeof WorkTicketManifestReqSchema>;

export const WorkTicketManifestResSchema = z.object({ ok: z.boolean(), status: z.string() });
export type WorkTicketManifestRes = z.infer<typeof WorkTicketManifestResSchema>;

// GET /api/work/generator/ticketPdfs — the caller's own uploads + manifest summary.
export const WorkTicketRowSchema = z.object({
  id: z.string(),
  file_name: z.string(),
  status: z.string(),
  created_at: z.string(),
  codes: z.array(WorkTicketCodeSchema),
  attendee_name: z.string(),
  nominative: z.string(),
  event_name: z.string(),
  country: z.string(),
  flags: z.array(z.string()),
});
export type WorkTicketRow = z.infer<typeof WorkTicketRowSchema>;

export const WorkTicketListResSchema = z.object({ tickets: z.array(WorkTicketRowSchema) });
export type WorkTicketListRes = z.infer<typeof WorkTicketListResSchema>;
